<div class="pt-3 md:px-4 px-2 pb-8 safe-area-margin">
  <h1 class="text-base md:text-lg text-900 flex align-items-center mb-0 line-height-1">
    {{ 'COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.TITLE' | translate }}
  </h1>
  <div class="mt-1 mb-3">
    <app-breadcrumb />
  </div>

  <div class="surface-0 border-round shadow-1 mt-3">
    <div class="px-2 md:px-3 pt-3">
      <div class="flex justify-content-between align-items-center mb-3">
        <div>
          <p class="text-500 mt-0 mb-0">
            {{ 'COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.SUBTITLE' | translate }}
          </p>
        </div>
        <div class="flex gap-2">
          <p-button [label]="'COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.EXPAND_ALL' | translate"
            icon="pi pi-plus"
            [text]="true"
            size="small"
            (onClick)="expandAll()"
            aria-label="expand-all-rows-button" />
          <p-button [label]="'COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.COLLAPSE_ALL' | translate"
            icon="pi pi-minus"
            [text]="true"
            size="small"
            (onClick)="collapseAll()"
            aria-label="collapse-all-rows-button" />
        </div>
      </div>

      <!-- Staff Table with Row Expansion -->
      <p-table
        [value]="staffList"
        [loading]="loading"
        [paginator]="true"
        [rows]="pageSize"
        [totalRecords]="totalRecords"
        [lazy]="true"
        (onPage)="onPageChange($event)"
        [rowsPerPageOptions]="[10, 25, 50]"
        [showCurrentPageReport]="true"
        currentPageReportTemplate="{first} - {last} of {totalRecords}"
        dataKey="staff_id"
        [expandedRowKeys]="expandedRows"
        (onRowExpand)="onRowExpand($event)"
        (onRowCollapse)="onRowCollapse($event)"
        styleClass="p-datatable-gridlines"
        [tableStyle]="{ 'min-width': '60rem' }"
        aria-label="staff-plan-assignments-table">

        <!-- Expand Column -->
        <ng-template #header>
          <tr>
            <th style="width: 3rem" aria-label="expand-column"></th>
            <th pSortableColumn="staff_name" aria-label="staff-name-column">
              {{ 'COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.STAFF_NAME' | translate }}
              <p-sortIcon field="staff_name" />
            </th>
            <th aria-label="staff-email-column">
              {{ 'COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.EMAIL' | translate }}
            </th>
            <th aria-label="staff-mobile-column">
              {{ 'COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.MOBILE' | translate }}
            </th>
            <th aria-label="staff-department-column">
              {{ 'COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.DEPARTMENT' | translate }}
            </th>
            <th aria-label="staff-designation-column">
              {{ 'COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.DESIGNATION' | translate }}
            </th>
            <th aria-label="staff-status-column">
              {{ 'COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.STATUS' | translate }}
            </th>
            <th aria-label="assigned-plans-count-column">
              {{ 'COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.ASSIGNED_PLANS_COUNT' | translate }}
            </th>
          </tr>
        </ng-template>

        <ng-template #body let-staff let-expanded="expanded">
          <tr>
            <td>
              <p-button type="button"
                [pRowToggler]="staff"
                [text]="true"
                [rounded]="true"
                [plain]="true"
                [icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
                [attr.aria-label]="'expand-row-' + staff.staff_id" />
            </td>
            <td>
              <span class="font-semibold">{{ staff.staff_name }}</span>
            </td>
            <td>{{ staff.staff_email }}</td>
            <td>{{ staff.staff_mobile }}</td>
            <td>{{ staff.staff_department }}</td>
            <td>{{ staff.staff_designation }}</td>
            <td>
              <p-tag [value]="staff.staff_status"
                [severity]="getStaffStatusSeverity(staff.staff_status)"
                [attr.aria-label]="'staff-status-' + staff.staff_status" />
            </td>
            <td>
              <div class="flex align-items-center gap-2">
                @if (hasPlans(staff)) {
                  <p-tag [value]="getPlansCount(staff)"
                    severity="info"
                    [attr.aria-label]="'plans-count-' + staff.staff_id" />
                }
                @if (!hasPlans(staff)) {
                  <p-button [label]="'COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.ASSIGN_PLAN' | translate"
                    icon="pi pi-plus"
                    size="small"
                    (onClick)="openAssignPlanDialog(staff)"
                    [attr.aria-label]="'assign-plan-button-' + staff.staff_id" />
                }
              </div>
            </td>
          </tr>
        </ng-template>

        <!-- Row Expansion Template -->
        <ng-template #expandedrow let-staff>
          <tr>
            <td colspan="9">
              <div class="p-3">
                @if (hasPlans(staff)) {
                  <div>
                    <h5 class="text-900 font-semibold mb-3">
                      {{ 'COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.ASSIGNED_PLANS' | translate }}
                    </h5>
                    <!-- Plans Table -->
                    <p-table
                      [value]="staff.plans"
                      styleClass="p-datatable-sm"
                      [tableStyle]="{ 'min-width': '50rem' }"
                      aria-label="staff-assigned-plans-table">
                      <ng-template pTemplate="header">
                        <tr>
                          <th aria-label="plan-name-column">
                            {{ 'COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.PLAN_NAME' | translate }}
                          </th>
                          <th aria-label="plan-frequency-column">
                            {{ 'COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.FREQUENCY' | translate }}
                          </th>
                          <th aria-label="plan-status-column">
                            {{ 'COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.PLAN_STATUS' | translate }}
                          </th>
                          <th aria-label="assignment-status-column">
                            {{ 'COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.ASSIGNMENT_STATUS' | translate }}
                          </th>
                          <th aria-label="start-date-column">
                            {{ 'COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.START_DATE' | translate }}
                          </th>
                          <th aria-label="end-date-column">
                            {{ 'COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.END_DATE' | translate }}
                          </th>
                        </tr>
                      </ng-template>
                      <ng-template pTemplate="body" let-plan>
                        <tr>
                          <td>
                            <span class="font-semibold">{{ plan.plan_name }}</span>
                          </td>
                          <td>{{ plan.plan_freq }}</td>
                          <td>
                            <p-tag [value]="plan.plan_status"
                              [severity]="getPlanStatusSeverity(plan.plan_status)"
                              [attr.aria-label]="'plan-status-' + plan.plan_status" />
                          </td>
                          <td>
                            <p-tag [value]="plan.plan_staff_assignment_status"
                              [severity]="getAssignmentStatusSeverity(plan.plan_staff_assignment_status)"
                              [attr.aria-label]="'assignment-status-' + plan.plan_staff_assignment_status" />
                          </td>
                          <td>{{ plan.plan_start_date | date: 'short' }}</td>
                          <td>{{ plan.plan_end_date | date: 'short' }}</td>
                        </tr>
                      </ng-template>
                      <ng-template pTemplate="emptymessage">
                        <tr>
                          <td colspan="6" class="text-center py-4">
                            <i class="pi pi-info-circle text-400 text-2xl mb-2"></i>
                            <p class="text-500 m-0">
                              {{ 'COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.NO_PLANS' | translate }}
                            </p>
                          </td>
                        </tr>
                      </ng-template>
                    </p-table>
                  </div>
                } @else {
                  <div class="text-center py-4">
                    <i class="pi pi-info-circle text-400 text-3xl mb-3"></i>
                    <p class="text-600 font-semibold mb-2">
                      {{ 'COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.NO_PLAN_ASSIGNED' | translate }}
                    </p>
                    <p-button [label]="'COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.ASSIGN_PLAN' | translate"
                      icon="pi pi-plus"
                      (onClick)="openAssignPlanDialog(staff)"
                      [attr.aria-label]="'assign-plan-button-expanded-' + staff.staff_id" />
                  </div>
                }

              </div>
            </td>
          </tr>
        </ng-template>

        <!-- Empty State -->
        <ng-template #emptymessage>
          <tr>
            <td colspan="9" class="text-center py-5">
              <i class="pi pi-users text-400 text-5xl mb-3"></i>
              <p class="text-600 font-semibold text-lg mb-2">
                {{ 'COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.NO_STAFF' | translate }}
              </p>
              <p class="text-500 m-0">
                {{ 'COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.NO_STAFF_DESC' | translate }}
              </p>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  </div>
</div>

<!-- Assign Plan Dialog -->
<p-dialog
  [visible]="showAssignDialog"
  [header]="'COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.ASSIGN_PLAN_DIALOG_TITLE' | translate"
  [modal]="true"
  [style]="{ width: '450px' }"
  [draggable]="false"
  [resizable]="false"
  aria-label="assign-plan-dialog">

  <div class="flex flex-column gap-3">
    <div>
      <label class="font-semibold text-900 mb-2 block">
        {{ 'COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.STAFF_NAME' | translate }}
      </label>
      <p class="text-600 m-0">{{ selectedStaffForAssignment?.staff_name }}</p>
    </div>

    <div>
      <label for="plan-select" class="font-semibold text-900 mb-2 block">
        {{ 'COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.SELECT_PLAN' | translate }}
        <sup class="text-red-500">*</sup>
      </label>
      <p-select inputId="plan-select"
        [(ngModel)]="selectedPlanId"
        [options]="availablePlans"
        optionLabel="name_pri_lang"
        optionValue="id"
        appendTo="body"
        [placeholder]="'COMMISSION_MANAGEMENT.STAFF_ASSIGNMENTS.SELECT_PLAN_PLACEHOLDER' | translate"
        [loading]="loadingPlans"
        [filter]="true"
        styleClass="w-full"
        aria-label="select-commission-plan" />
    </div>
  </div>

  <ng-template pTemplate="footer">
    <p-button [label]="'COMMISSION_MANAGEMENT.COMMON.CANCEL' | translate"
      icon="pi pi-times"
      [outlined]="true"
      severity="secondary"
      (onClick)="closeAssignDialog()"
      aria-label="cancel-assign-button" />
    <p-button [label]="'COMMISSION_MANAGEMENT.COMMON.ASSIGN_PLAN' | translate"
      icon="pi pi-check"
      [loading]="assigningPlan"
      [disabled]="!selectedPlanId"
      (onClick)="assignPlan()"
      aria-label="confirm-assign-button" />
  </ng-template>
</p-dialog>

<p-toast />
